import { useCallback, useState } from "react";

import { Button, Dropdown } from "@/components";
import { useAppProperties } from "@/hooks";
import { CaretDownMD, LogOut } from "@/icons";
import { useTranslate } from "@/providers/context";
import { cn } from "@/utils";

import { useCommunityFeed } from "../../providers/context/useCommunityFeed";

const ICON_PROPS = {
  height: 20,
  width: 20,
};

export const JoinLeaveButton = ({ className }: { className?: string }) => {
  const { data: appProperties } = useAppProperties();
  const { translate } = useTranslate();

  const { handleJoinCommunity, handleLeaveCommunity, group } =
    useCommunityFeed();

  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [joinIsLoading, setJoinIsLoading] = useState(false);

  const handleAction = useCallback(async () => {
    setJoinIsLoading(true);
    setDropdownIsOpen(false);
    try {
      if (group.isJoined) {
        await handleLeaveCommunity();
      } else {
        await handleJoinCommunity();
      }
    } finally {
      setJoinIsLoading(false);
    }
  }, [
    group.isJoined,
    handleJoinCommunity,
    handleLeaveCommunity,
    setJoinIsLoading,
    setDropdownIsOpen,
  ]);

  const groupLabel = appProperties?.CommunityGroupLabelSingular ?? "group";
  const actionLabel = `${translate(group.isJoined ? "Leave" : "Join")} ${groupLabel}`;

  if (group.isJoined) {
    return (
      <Dropdown
        asChild
        open={dropdownIsOpen}
        onOpenChange={setDropdownIsOpen}
        options={[
          {
            text: actionLabel,
            icon: <LogOut {...ICON_PROPS} />,
            color: "error",
            onClick: handleAction,
          },
        ]}
        optionsClassName="capitalize"
        contentProps={{
          align: "start",
          sideOffset: 12,
        }}
      >
        <Button
          className={cn("w-fit", className)}
          variant="secondary"
          icon={<CaretDownMD {...ICON_PROPS} />}
          loading={joinIsLoading}
          disabled={joinIsLoading}
        >
          {translate("Joined")}
        </Button>
      </Dropdown>
    );
  }

  return (
    <Button
      className={cn("w-fit capitalize", className)}
      onClick={handleAction}
      loading={joinIsLoading}
      disabled={joinIsLoading}
    >
      {actionLabel}
    </Button>
  );
};
